<script lang="ts" setup>
import { Announcement02Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UIAvatar, UITextSmMedium, UITextSmRegular } from '@gohighlevel/ghl-ui'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BadgedAvatar from './avatar/BadgedAvatar.vue'

const props = defineProps({
  items: {
    type: Array<any>,
    required: true,
  },
  command: {
    type: Function,
    required: true,
  },
})
const { t } = useI18n()
const selectedIndex = ref(0)
watch(
  () => props.items,
  () => {
    selectedIndex.value = 0
  }
)
function onKeyDown({ event }) {
  if (event.key === 'ArrowUp') {
    upHandler()
    return true
  }
  if (event.key === 'ArrowDown') {
    downHandler()
    return true
  }
  if (event.key === 'Enter') {
    enterHandler()
    return true
  }
  return false
}
function upHandler() {
  selectedIndex.value =
    (selectedIndex.value + props.items.length - 1) % props.items.length
}
function downHandler() {
  selectedIndex.value = (selectedIndex.value + 1) % props.items.length
}
function enterHandler() {
  selectItem(selectedIndex.value)
}

function selectItem(index) {
  const item = props.items[index]
  if (item) {
    if (item.type === 'broadcast') {
      props.command({
        label: item.name,
        type: item.type,
      })
    } else {
      props.command({
        id: item.contactId,
        label: item.fullName,
        slug: item.slug,
      })
    }
  }
}
defineExpose({ onKeyDown })
</script>

<template>
  <div>
    <div v-if="items?.length" class="items w-[316px]">
      <div
        v-for="(item, index) in items"
        :key="item.contactId ?? item.name"
        :class="{ 'is-selected': index === selectedIndex }"
        class="flex cursor-pointer flex-row items-center px-4 py-2 text-communities-font-primary"
        role="listbox"
        :aria-label="t('common.ariaLabels.mentionItem')"
        tabindex="0"
        @mouseover="selectedIndex = index"
        @mouseleave="selectedIndex = -1"
        @click="selectItem(index)"
        @keyup.enter="selectItem(index)"
      >
        <UIAvatar
          v-if="item.type === 'broadcast'"
          :size="28"
          class="flex items-center justify-center bg-communities-fill text-base font-medium text-communities-font-secondary"
        >
          <Announcement02Icon class="h-5 w-5" />
        </UIAvatar>
        <BadgedAvatar
          v-else
          :avatar-url="item.avatar"
          :full-name="item.fullName"
          :size="28"
          :show-badge="false"
          :role="item.role"
        />

        <UITextSmMedium class="ml-3 truncate font-semibold">
          @{{ item.type === 'broadcast' ? item.name : item.fullName }}
        </UITextSmMedium>
        <UITextSmRegular v-if="item.type === 'broadcast'" class="truncate">
          &nbsp; {{ t('common.notifyAll') }}
        </UITextSmRegular>
      </div>
    </div>
  </div>
</template>

<style scoped>
.items {
  position: relative;
  border-radius: 0.5rem;
  background: var(--background);
  color: rgba(0, 0, 0, 0.8);
  overflow: hidden;
  font-size: 0.9rem;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0px 10px 20px rgba(0, 0, 0, 0.1);
}

.is-selected {
  background-color: var(--fill);
}
</style>
